import { useState, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Stack,
  Alert,
  Divider,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import JoditEditor from "jodit-react";
import { useLocation, useNavigate } from "react-router-dom"; // Add useLocation
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import {
  createOrganCategory,
  updateOrganCategory,
} from "../../redux/slices/organCategorySlice";
import Notification from "../../components/common/Notification"; // Add this import

// Constants
const MAX_IMAGE_SIZE = 100 * 1024;
const MIN_IMAGE_SIZE = 1 * 1024;

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3), // Add padding to all sides
  margin: theme.spacing(3), // Add margin to all sides
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  position: "sticky",
  top: 0,
  zIndex: 1100,
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: "32px",
  marginBottom: "24px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
    marginBottom: 0,
  },
}));

const ImageDropZone = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 300,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const Tag = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  margin: "4px",
  padding: "4px 12px",
  backgroundColor: theme.palette.primary.light,
  borderRadius: "16px",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    marginLeft: "8px",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

const TagsContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "8px 0",
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UploadIconContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

const Form = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData; // Get editData from navigation state
  const dispatch = useDispatch();
  const editor = useRef(null);
  const { loading } = useAppSelector((state) => state.organCategory);

  // Form State initialized with editData if present
  const [formData, setFormData] = useState({
    name: editData?.name || "",
    slug: editData?.slug || "",
    status: editData?.status || "active",
    image: null,
    preview: editData?.image || null,
    description: editData?.description || "",
    seoTitle: editData?.seoTitle || "",
    seoDescription: editData?.seoDescription || "",
    seoKeywords: editData?.seoKeywords || "",
  });

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);

  // Editor Config
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter organ description here...",
      height: 300,
      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "link",
        "image",
      ],
      width: "100%",
    }),
    []
  );

  // Handlers
  const handleImageChange = (event) => {
    const file = event.target?.files?.[0] || event;
    if (!file) return;

    // Check if file is actually a File/Blob object
    if (!(file instanceof Blob)) {
      setErrors((prev) => ({
        ...prev,
        image: "Invalid file format",
      }));
      return;
    }

    if (file.size < MIN_IMAGE_SIZE || file.size > MAX_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be between 1KB and 100KB",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        image: file,
        preview: reader.result,
      }));
      setErrors((prev) => ({ ...prev, image: "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      preview: null,
    }));
  };

  const handleSEODescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData((prev) => ({
        ...prev,
        seoDescription: value,
      }));
      setErrors((prev) => ({
        ...prev,
        seoDescription: "",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        seoDescription: "Description cannot exceed 100 characters",
      }));
    }
  };

  // Update the handleKeywordChange function
  const handleKeywordChange = (e) => {
    const value = e.target.value;

    if (value.includes(",")) {
      const keywords = value
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);

      if (keywords.length <= 15) {
        setFormData((prev) => ({
          ...prev,
          seoKeywords: keywords.join(","),
        }));
        setErrors((prev) => ({ ...prev, seoKeywords: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          seoKeywords: "Cannot exceed 15 keywords",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        seoKeywords: value,
      }));
    }
  };

  // Update the onClose/navigation logic
  const handleClose = () => {
    navigate("/organ-category"); // Navigate back to list
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous notifications
    setNotification(null);

    try {
      const formDataObj = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "image" && formData[key] instanceof Blob) {
          formDataObj.append(key, formData[key]);
        } else if (key !== "preview" && formData[key]) {
          formDataObj.append(key, formData[key].toString().trim());
        }
      });

      // Dispatch create or update based on editData
      const action = editData
        ? updateOrganCategory({ id: editData._id, data: formDataObj })
        : createOrganCategory(formDataObj);

      const result = await dispatch(action).unwrap();

      if (result) {
        // Show success notification
        setNotification({
          type: "success",
          message: `Organ category ${
            editData ? "updated" : "created"
          } successfully!`,
        });

        // Navigate after a short delay to show success message
        setTimeout(() => {
          navigate("/organ-category", { replace: true });
        }, 1500);
      }
    } catch (err) {
      console.error("Form submission error:", err);

      // Show complete backend error
      setNotification({
        type: "error",
        message: err || "Operation failed. Please try again.",
      });
    }
  };

  // Update the name field handler to auto-generate slug
  const handleNameChange = (e) => {
    const nameValue = e.target.value;
    const slugValue = generateSlug(nameValue);

    setFormData((prev) => ({
      ...prev,
      name: nameValue,
      slug: slugValue, // Auto-generate slug from name
    }));
  };

  return (
    <StyledContainer>
      <StyledHeader>
        <IconButton onClick={handleClose} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          {editData ? "Edit" : "Add New"} Organ Category
        </Typography>
      </StyledHeader>

      <Box
        sx={{
          backgroundColor: "common.white",
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* First Row: Image Upload + Basic Info */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 3,
              "& > *": { flex: "1 1 50%" }, // Make both sections take equal width
            }}
          >
            {/* Left Side - Image Upload */}
            <StyledFormSection
              sx={{
                minWidth: "400px",
                maxWidth: "50%", // Limit maximum width
                height: "fit-content",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Organ Image
              </Typography>
              <Box sx={{ position: "relative" }}>
                <ImageDropZone
                  onClick={() => document.getElementById("image-input").click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleImageChange(file);
                  }}
                >
                  {formData.preview ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={formData.preview}
                        alt="Organ Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                      <CloseButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearImage();
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </CloseButton>
                    </Box>
                  ) : (
                    <UploadIconContainer>
                      <CloudUploadIcon
                        sx={{ fontSize: 64, color: "primary.main" }}
                      />
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        align="center"
                      >
                        Drag and drop your image here
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="center"
                      >
                        or click to browse
                      </Typography>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        Image size: 1KB - 100KB
                      </Typography>
                    </UploadIconContainer>
                  )}
                </ImageDropZone>
                <input
                  id="image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  style={{ display: "none" }}
                />
              </Box>
              {errors.image && (
                <FormHelperText error sx={{ ml: 1 }}>
                  {errors.image}
                </FormHelperText>
              )}
            </StyledFormSection>

            {/* Right Side - Basic Information */}
            <StyledFormSection
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "fit-content",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <TextField
                fullWidth
                label="Organ Name"
                variant="outlined"
                value={formData.name}
                onChange={handleNameChange} // Use new handler
                required
              />

              <TextField
                fullWidth
                label="Slug Title"
                variant="outlined"
                value={formData.slug}
                disabled // Make it read-only
                helperText="Auto-generated from organ name"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#666", // Better visibility for disabled text
                  },
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </StyledFormSection>
          </Box>

          {/* Second Row: Description Editor */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Description
            </Typography>
            <Box
              sx={{
                width: "100%",
                "& .jodit-container": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-workplace": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-wysiwyg": {
                  width: "100% !important",
                  maxWidth: "100%",
                  padding: "16px",
                },
                "& .jodit-toolbar__box": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-status-bar": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
              }}
            >
              <JoditEditor
                ref={editor}
                value={formData.description}
                config={editorConfig}
                onBlur={(newContent) => {
                  setFormData((prev) => ({
                    ...prev,
                    description: newContent,
                  }));
                }}
              />
            </Box>
          </StyledFormSection>

          {/* Third Row: SEO Title and Description */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              SEO Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: "flex", gap: 3 }}>
              <TextField
                fullWidth
                label="SEO Title"
                variant="outlined"
                value={formData.seoTitle}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    seoTitle: e.target.value,
                  }))
                }
              />
              <TextField
                fullWidth
                label="SEO Description"
                variant="outlined"
                multiline
                rows={3}
                value={formData.seoDescription}
                onChange={handleSEODescriptionChange}
                error={Boolean(errors.seoDescription)}
                helperText={
                  errors.seoDescription ||
                  `${formData.seoDescription.length}/100 characters`
                }
              />
            </Box>
          </StyledFormSection>

          {/* Fourth Row: SEO Keywords */}
          <StyledFormSection>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="SEO Keywords"
                variant="outlined"
                value={formData.seoKeywords}
                onChange={handleKeywordChange}
                error={Boolean(errors.seoKeywords)}
                helperText={
                  errors.seoKeywords ||
                  "Type keywords separated by commas. Example: keyword1,keyword2,keyword3 (max 15)"
                }
                placeholder="Type keywords separated by commas"
              />

              <TagsContainer>
                {formData.seoKeywords.split(",").map((keyword, index) =>
                  keyword.trim() ? (
                    <Tag key={index}>
                      <Typography variant="body2" component="span">
                        {keyword.trim()}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const keywords = formData.seoKeywords
                            .split(",")
                            .filter((_, i) => i !== index)
                            .join(",");
                          setFormData((prev) => ({
                            ...prev,
                            seoKeywords: keywords,
                          }));
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tag>
                  ) : null
                )}
              </TagsContainer>
            </FormControl>
          </StyledFormSection>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? "Saving..." : editData ? "Update" : "Create"}
              </Button>
            </Stack>
          </Box>
        </form>

        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
            autoClose={notification.type === "success"}
            duration={3000}
          />
        )}

        {loading && <Loader />}
      </Box>
    </StyledContainer>
  );
};

export default Form;
